import { useState, useCallback } from 'react';

/**
 * Custom hook for managing error states
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoClear - Whether to automatically clear errors after a delay (default: false)
 * @param {number} options.clearDelay - Delay in milliseconds before auto-clearing (default: 5000)
 * @param {Function} options.onError - Callback function when error is set
 * @returns {Object} Error state and handlers
 */
const useErrorHandler = (options = {}) => {
  const {
    autoClear = false,
    clearDelay = 5000,
    onError = null
  } = options;

  const [error, setError] = useState(null);
  const [hasError, setHasError] = useState(false);

  const setErrorState = useCallback((errorMessage, errorType = 'error') => {
    const errorObj = {
      message: errorMessage,
      type: errorType,
      timestamp: new Date().toISOString()
    };

    setError(errorObj);
    setHasError(true);

    // Call the onError callback if provided
    if (onError) {
      onError(errorObj);
    }

    // Auto-clear error if enabled
    if (autoClear && clearDelay > 0) {
      setTimeout(() => {
        clearError();
      }, clearDelay);
    }
  }, [autoClear, clearDelay, onError]);

  const clearError = useCallback(() => {
    setError(null);
    setHasError(false);
  }, []);

  const handleApiError = useCallback((apiError, fallbackMessage = 'An error occurred') => {
    let errorMessage = fallbackMessage;
    
    if (apiError?.response?.data?.message) {
      errorMessage = apiError.response.data.message;
    } else if (apiError?.response?.data?.error) {
      errorMessage = apiError.response.data.error;
    } else if (apiError?.message) {
      errorMessage = apiError.message;
    } else if (typeof apiError === 'string') {
      errorMessage = apiError;
    }

    // Determine error type based on status code
    let errorType = 'error';
    if (apiError?.response?.status) {
      const status = apiError.response.status;
      if (status >= 500) {
        errorType = 'error';
      } else if (status >= 400) {
        errorType = 'warning';
      } else {
        errorType = 'info';
      }
    }

    setErrorState(errorMessage, errorType);
  }, [setErrorState]);

  const handleAsyncOperation = useCallback(async (asyncFunction, fallbackMessage = 'Operation failed') => {
    try {
      clearError();
      return await asyncFunction();
    } catch (error) {
      handleApiError(error, fallbackMessage);
      throw error; // Re-throw to allow calling code to handle if needed
    }
  }, [clearError, handleApiError]);

  return {
    error,
    hasError,
    setError: setErrorState,
    clearError,
    handleApiError,
    handleAsyncOperation
  };
};

export default useErrorHandler;
